import { CheckCircle2, AlertTriangle, Clock } from 'lucide-react'

// WHERE A CLIENT LANDS AFTER PAYING A BILL WAVBEE SENT THEM (D29 + D31).
//
// Both gateway links return here, and the server has already asked the gateway
// itself whether the money moved — nothing on this page is decided by what the
// browser carried back.
//
// THREE OUTCOMES, AND THE MIDDLE ONE IS NOT A FAILURE. A charge the gateway has
// not confirmed yet is swept every 15 minutes and settles itself; telling the
// client it failed would send them to pay a second time, which is the one thing
// this whole flow exists to prevent. So it says "not confirmed yet", names no
// amount, and offers support rather than a retry.
//
// `closed_link` is the second link completed after the first had settled. The
// money is at the gateway, unbooked, and a human has already been alarmed —
// the client is told that plainly rather than shown a receipt for a payment
// wavbee is about to return.

interface Props {
  outcome: 'paid' | 'unverified' | 'closed_link'
  reference: string
  total: string
  invoice_number: string | null
  support_email: string
}

export default function PaymentRequestReceipt({
  outcome, reference, total, invoice_number, support_email,
}: Props) {
  return (
    <div className="max-w-lg mx-auto px-5 py-20">
      {outcome === 'paid' && (
        <Panel
          icon={<CheckCircle2 className="w-7 h-7 text-emerald-400" />}
          title="Payment received"
        >
          <p>
            Thank you — <strong>{total}</strong> against <strong>{reference}</strong> has been
            received.
          </p>
          {invoice_number ? (
            <p>
              Your tax invoice <strong>{invoice_number}</strong> has been issued and emailed to you.
            </p>
          ) : (
            <p>Your tax invoice is being issued and will be emailed to you shortly.</p>
          )}
        </Panel>
      )}

      {outcome === 'unverified' && (
        <Panel
          icon={<Clock className="w-7 h-7 text-amber-400" />}
          title="Payment not confirmed yet"
        >
          <p>
            We have not been able to confirm a payment against <strong>{reference}</strong> yet.
          </p>
          {/* DO NOT PAY AGAIN is the whole message. A charge the gateway has not
              reported yet settles itself within about fifteen minutes. */}
          <p>
            If you have just paid, please do <strong>not</strong> pay again — the payment settles on
            its own within about fifteen minutes and you will be emailed a tax invoice. If nothing
            arrives, write to <a href={`mailto:${support_email}`} className="underline">{support_email}</a>{' '}
            quoting {reference}.
          </p>
        </Panel>
      )}

      {outcome === 'closed_link' && (
        <Panel
          icon={<AlertTriangle className="w-7 h-7 text-red-400" />}
          title="This link had already been settled"
        >
          <p>
            <strong>{reference}</strong> was already paid through the other link, so this one was
            closed. If a payment was taken on it, it will be returned to you.
          </p>
          <p>
            Our team has already been notified. Write to{' '}
            <a href={`mailto:${support_email}`} className="underline">{support_email}</a> quoting{' '}
            {reference} if you would like it confirmed.
          </p>
        </Panel>
      )}
    </div>
  )
}

function Panel({ icon, title, children }: {
  icon: React.ReactNode
  title: string
  children: React.ReactNode
}) {
  return (
    <div className="p-6 rounded-xl border border-white/10 bg-white/[0.03] space-y-3">
      <div className="flex items-center gap-3">
        {icon}
        <h1 className="text-xl font-semibold text-white font-display">{title}</h1>
      </div>
      <div className="space-y-2 text-sm text-white/60">{children}</div>
    </div>
  )
}
